import * as React from 'react'
import { Link } from 'react-router-dom'
import { ArrowLeft, Flag, Timer, Trophy, Users } from 'lucide-react'
import {
  formatRaceTime,
  HEIGHT,
  LAPS,
  NO_INPUT,
  newRace,
  onTrack,
  raceMs,
  start,
  step,
  TRACK,
  TRACK_HALF,
  WIDTH,
  type Input,
  type RaceState,
  type Status,
} from '@/lib/race-game'
import { sb } from '@/lib/supabase'
import { useAuth } from '@/auth/auth-provider'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { ErrorNote } from '@/components/page'

/**
 * Racing (migrations/0062_racing_game.sql).
 *
 * Missile Defence's shape — one player, rules in a lib, the race in a ref — plus
 * the one thing that game does not have: a result worth keeping. A finished race
 * writes its time to `race_times`, and the page shows exactly two numbers back,
 * the player's own best and their team's best. Both come from the database, not
 * from this tab, so they stand across page loads; and neither says anything
 * about anybody else, because the policy and `race_team_best` do not let it.
 *
 * There is no group picker and no other car on the track, on purpose: everyone
 * races alone and the clock is the opponent.
 */
export function RacingPage() {
  const { session, currentTeam, memberships } = useAuth()
  // A DPL admin can open any team without being on it (memberId is '' then),
  // and 0062 refuses a time filed there. Know that before the race, not after.
  const onTeam = Boolean(memberships.find((m) => m.team.id === currentTeam?.id)?.memberId)
  const canvasRef = React.useRef<HTMLCanvasElement | null>(null)
  const race = React.useRef<RaceState>(newRace())
  const keys = React.useRef<Input>({ ...NO_INPUT })
  const [status, setStatus] = React.useState<Status>('ready')
  const [lap, setLap] = React.useState(0)
  const [clock, setClock] = React.useState(0)
  const [lastMs, setLastMs] = React.useState<number | null>(null)
  const [myBest, setMyBest] = React.useState<number | null>(null)
  const [teamBest, setTeamBest] = React.useState<number | null>(null)
  const [newBest, setNewBest] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)
  const userId = session?.user.id

  const loadBests = React.useCallback(async () => {
    if (!currentTeam || !userId) return
    const [mine, team] = await Promise.all([
      // RLS returns only the caller's rows anyway; the filter says what the
      // query means rather than leaning on that.
      sb()
        .from('race_times')
        .select('duration_ms')
        .eq('user_id', userId)
        .order('duration_ms')
        .limit(1)
        .maybeSingle(),
      sb().rpc('race_team_best', { p_team_id: currentTeam.id }),
    ])
    if (mine.error) return setError(mine.error.message)
    if (team.error) return setError(team.error.message)
    setMyBest((mine.data?.duration_ms as number | undefined) ?? null)
    setTeamBest((team.data as number | null) ?? null)
  }, [currentTeam, userId])

  React.useEffect(() => {
    void loadBests()
  }, [loadBests])

  const finish = React.useCallback(
    async (ms: number) => {
      if (!currentTeam) return
      setLastMs(ms)
      if (!onTeam) return
      setNewBest(myBest == null || ms < myBest)
      const { error: err } = await sb().from('race_times').insert({ team_id: currentTeam.id, duration_ms: ms })
      if (err) return setError(err.message)
      await loadBests()
    },
    [currentTeam, onTeam, myBest, loadBests],
  )
  // The loop reads `finish` through a ref so it never restarts when the bests
  // load — restarting it mid-race would reset the frame clock.
  const finishRef = React.useRef(finish)
  finishRef.current = finish

  const begin = React.useCallback(() => {
    race.current = start()
    setLastMs(null)
    setNewBest(false)
    setError(null)
  }, [])

  // The track is drawn once, a pixel at a time, into its own canvas: it never
  // changes, and testing 25,000 pixels against the centre line every frame
  // would be the only expensive thing on the page.
  const trackLayer = React.useMemo(() => drawTrack(), [])

  React.useEffect(() => {
    let frame = 0
    let last = performance.now()
    const tick = (now: number) => {
      const before = race.current.status
      race.current = step(race.current, (now - last) / 1000, keys.current)
      last = now
      const r = race.current
      setStatus((s) => (s === r.status ? s : r.status))
      setLap((l) => (l === r.lap ? l : r.lap))
      // Tenths are enough for a ticking clock, and a re-render each frame for
      // the thousandths is not worth it; the final time is exact.
      const tenths = Math.floor(r.elapsed * 10)
      setClock((c) => (c === tenths ? c : tenths))
      if (before === 'racing' && r.status === 'finished') void finishRef.current(raceMs(r))
      if (canvasRef.current) draw(canvasRef.current, trackLayer, r)
      frame = requestAnimationFrame(tick)
    }
    frame = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(frame)
  }, [trackLayer])

  // The arrow keys, on the window so nobody has to click the canvas first.
  // Held state rather than events: the car reads the pedals every slice. Up on
  // the start screen starts the race; nothing restarts one, so the last
  // keypress of a race cannot wipe its time off the screen.
  React.useEffect(() => {
    const map: Record<string, keyof Input> = {
      ArrowUp: 'up',
      ArrowDown: 'down',
      ArrowLeft: 'left',
      ArrowRight: 'right',
    }
    const onKey = (down: boolean) => (e: KeyboardEvent) => {
      const k = map[e.key]
      if (!k) return
      const t = e.target as HTMLElement | null
      if (t && (t.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(t.tagName))) return
      // Arrows scroll the page, which would carry the track away mid-corner.
      e.preventDefault()
      keys.current = { ...keys.current, [k]: down }
      if (down && k === 'up' && race.current.status === 'ready') race.current = start()
    }
    const onDown = onKey(true)
    const onUp = onKey(false)
    // A key released while the tab is in the background never sends keyup;
    // without this the car would come back with the gas jammed down.
    const onBlur = () => (keys.current = { ...NO_INPUT })
    window.addEventListener('keydown', onDown)
    window.addEventListener('keyup', onUp)
    window.addEventListener('blur', onBlur)
    return () => {
      window.removeEventListener('keydown', onDown)
      window.removeEventListener('keyup', onUp)
      window.removeEventListener('blur', onBlur)
    }
  }, [])

  return (
    <div className="mx-auto w-full max-w-5xl px-4 py-6 sm:px-6">
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <Button variant="ghost" size="icon-sm" asChild aria-label="Back to Games">
          <Link to="/games">
            <ArrowLeft />
          </Link>
        </Button>
        <div className="min-w-0">
          <h1 className="text-xl font-semibold leading-tight">Racing</h1>
          <p className="text-xs text-muted-foreground">Up is the gas, down the brake, left and right steer.</p>
        </div>
        <div className="ml-auto flex flex-wrap items-center gap-2">
          <Badge variant="secondary" className="tabular-nums">
            <Flag className="mr-1 h-3.5 w-3.5" />
            Lap {Math.min(lap + 1, LAPS)} / {LAPS}
          </Badge>
          <Badge variant="secondary" className="tabular-nums">
            <Timer className="mr-1 h-3.5 w-3.5" />
            {status === 'finished' && lastMs != null ? formatRaceTime(lastMs) : formatRaceTime(clock * 100)}
          </Badge>
        </div>
      </div>

      {error && (
        <div className="mb-4">
          <ErrorNote error={error} />
        </div>
      )}

      <div className="relative overflow-hidden rounded-lg border bg-card">
        <canvas
          ref={canvasRef}
          width={PX_W}
          height={PX_H}
          className="block h-auto w-full [image-rendering:pixelated]"
          aria-label="Racing track"
        />
        {status !== 'racing' && (
          <div className="absolute inset-0 flex items-center justify-center bg-background/70 p-4">
            <div className="max-w-sm space-y-3 text-center">
              {status === 'ready' && (
                <>
                  <p className="text-lg font-semibold">{LAPS} laps, against the clock.</p>
                  <p className="text-sm text-muted-foreground">
                    Stay on the grey. The grass slows you to a crawl, and a lap only counts if you go all the way
                    round.
                  </p>
                  {!onTeam && (
                    <p className="text-xs text-muted-foreground">
                      You are not on {currentTeam?.name ?? 'this team'}, so your time will not be kept here.
                    </p>
                  )}
                </>
              )}
              {status === 'finished' && lastMs != null && (
                <>
                  <Trophy className="mx-auto h-8 w-8 text-primary" />
                  <p className="text-lg font-semibold tabular-nums">{formatRaceTime(lastMs)}</p>
                  {newBest && <p className="text-sm text-muted-foreground">A new personal best.</p>}
                </>
              )}
              <Button onClick={begin}>{status === 'ready' ? 'Start' : 'Race again'}</Button>
              {status === 'ready' && <p className="text-xs text-muted-foreground">or press up</p>}
            </div>
          </div>
        )}
      </div>

      <div className="mt-4 grid gap-3 sm:grid-cols-2">
        <BestTime icon={<Timer className="h-4 w-4" />} label="Your best" ms={myBest} />
        <BestTime icon={<Users className="h-4 w-4" />} label={`Best on ${currentTeam?.name ?? 'your team'}`} ms={teamBest} />
      </div>
    </div>
  )
}

function BestTime({ icon, label, ms }: { icon: React.ReactNode; label: string; ms: number | null }) {
  return (
    <div className="flex items-center gap-3 rounded-lg border bg-card px-4 py-3">
      <span className="text-muted-foreground">{icon}</span>
      <span className="text-sm text-muted-foreground">{label}</span>
      <span className="ml-auto font-semibold tabular-nums">{ms == null ? 'No time yet' : formatRaceTime(ms)}</span>
    </div>
  )
}

/**
 * The look is the 2600's, which is why these are fixed colours rather than
 * theme tokens: chunky pixels in a handful of flat colours ARE the request, and
 * a track that went navy in dark mode would stop looking like grass and tarmac.
 * The page chrome around the canvas still follows the theme.
 */
const GRASS = '#3c7a1c'
const TARMAC = '#8a8a8a'
const CAR = '#d23c2c'
const WHEEL = '#1c1c1c'

/** World units per pixel. The canvas is drawn this coarse and stretched. */
const SCALE = 4
const PX_W = WIDTH / SCALE
const PX_H = HEIGHT / SCALE

function drawTrack(): HTMLCanvasElement {
  const layer = document.createElement('canvas')
  layer.width = PX_W
  layer.height = PX_H
  const ctx = layer.getContext('2d')
  if (!ctx) return layer
  for (let py = 0; py < PX_H; py++) {
    for (let px = 0; px < PX_W; px++) {
      ctx.fillStyle = onTrack((px + 0.5) * SCALE, (py + 0.5) * SCALE) ? TARMAC : GRASS
      ctx.fillRect(px, py, 1, 1)
    }
  }
  // The finish line, chequered, across the track at the first point. It runs
  // across the first segment, which is horizontal.
  const line = TRACK[0]
  const x = Math.round(line.x / SCALE)
  const top = Math.round((line.y - TRACK_HALF) / SCALE)
  for (let i = 0; i < Math.round((2 * TRACK_HALF) / SCALE); i++) {
    for (let j = 0; j < 2; j++) {
      ctx.fillStyle = (i + j) % 2 === 0 ? '#ffffff' : '#000000'
      ctx.fillRect(x + j, top + i, 1, 1)
    }
  }
  return layer
}

/**
 * The car is a handful of pixels, turned in sixteen steps: a 5x3 body with a
 * wheel at each corner. Rotating whole cells and rounding is what makes it
 * jagged, which is the point.
 */
const BODY: ReadonlyArray<[number, number]> = [-2, -1, 0, 1, 2].flatMap((dx) =>
  [-1, 0, 1].map((dy) => [dx, dy] as [number, number]),
)
const WHEELS: ReadonlyArray<[number, number]> = [
  [-2, -2],
  [-2, 2],
  [2, -2],
  [2, 2],
]

function draw(canvas: HTMLCanvasElement, track: HTMLCanvasElement, r: RaceState) {
  const ctx = canvas.getContext('2d')
  if (!ctx) return
  ctx.imageSmoothingEnabled = false
  ctx.drawImage(track, 0, 0)
  const steps = 16
  const a = (Math.round((r.heading / (2 * Math.PI)) * steps) * 2 * Math.PI) / steps
  const cos = Math.cos(a)
  const sin = Math.sin(a)
  const cx = r.x / SCALE
  const cy = r.y / SCALE
  const cell = (cells: ReadonlyArray<[number, number]>, colour: string) => {
    ctx.fillStyle = colour
    for (const [dx, dy] of cells) {
      ctx.fillRect(Math.round(cx + dx * cos - dy * sin), Math.round(cy + dx * sin + dy * cos), 1, 1)
    }
  }
  cell(WHEELS, WHEEL)
  cell(BODY, CAR)
}
